import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useSetPageTitle } from "../../hooks/useSetPageTitle";
import { AppLayout } from "../../components/layout/app-layout";
import InsertionOrdersTab from "./tabs/insertion-orders-tab";
import InvoicesTab from "./tabs/invoices-tab";
import BillingTab from "./tabs/billing-tab";
import CommissionsTab from "./commissions/commissions-tab";
import { NavbarTab } from "src/components/layout/app-navbar";
import { RootState } from "../../redux/redux-store";

const BILLING_COMMISSION_ADMIN = "billing_commission_admin";

const BillingPage = () => {
  useSetPageTitle('Billing');
  const navigate = useNavigate();
  const userState = useSelector((state: RootState) => state.userState);
  const hasBillingCommissionAdmin = userState.permissions?.groups?.includes(BILLING_COMMISSION_ADMIN) ?? false;

  const { tab } = useParams<{ tab?: string }>();

  const billingTabs: NavbarTab[] = [
    {
      id: '',
      key: 'billing',
      label: 'Billing',
    },
    {
      id: 'insertion-orders',
      key: 'insertion-orders',
      label: 'Insertion Orders',
    },
    {
      id: 'invoices',
      key: 'invoices',
      label: 'Invoices',
    },
    ...(hasBillingCommissionAdmin
      ? [{ id: 'commissions' as const, key: 'commissions' as const, label: 'Commissions' }]
      : []),
  ];

  const validTabs = billingTabs.map(t => t.id);
  type TabType = typeof validTabs[number];

  const currentTab: TabType = validTabs.includes(tab as TabType)
    ? (tab as TabType)
    : '';

  useEffect(() => {
    if (tab === 'commissions' && !hasBillingCommissionAdmin) {
      navigate('/billing', { replace: true });
    }
  }, [tab, hasBillingCommissionAdmin, navigate]);

  const handleTabChange = (tabId: string) => {
    navigate(`/billing/${tabId}`);
  };


  // New layout for org admins and regular users
  return (
    <AppLayout
      activePage="/billing"
      navbarProps={{
        showBackButton: true,
        backPath: "/billing",
        tabs: billingTabs,
        activeTab: currentTab,
        onTabChange: handleTabChange,
      }}
    >
      {/* No overflow-hidden here: it clips full-bleed children (e.g. actions strip negative margins). */}
      <div className="flex h-[calc(100vh-8rem)] min-h-0 flex-col">
        {currentTab === '' && <BillingTab />}
        {currentTab === 'insertion-orders' && <InsertionOrdersTab />}
        {currentTab === 'invoices' && <InvoicesTab />}
        {currentTab === 'commissions' && <CommissionsTab />}
      </div>
    </AppLayout>
  );
};

export default BillingPage;